<script lang="ts">
  // ============================================================
  // ✅ Svelte 5 Runes Component
  // ------------------------------------------------------------
  // 이 컴포넌트는 Svelte 5 runes 문법을 사용합니다.
  // ============================================================

  import { router } from '@inertiajs/svelte'
  import Button from '@/components/ui/Button.svelte'
  import MarkdownToolbar from '@/components/MarkdownToolbar.svelte'
  import PrivacySelectorWithPreview from '@/components/ui/PrivacySelectorWithPreview.svelte'
  import { toasts } from '@/stores/toasts'

  interface Category {
    id: number;
    name: string;
    color: string;
  }

  interface Board {
    slug: string;
    name: string;
  }

  interface EditablePost {
    id: number;
    title: string;
    content: string;
    privacy?: string;
    categories?: Category[];
  }

  type FormErrors = Record<string, string[] | string>;

  let {
    board = { slug: '', name: '' } as Board,
    post = {} as EditablePost,
    categories = [] as Category[],
    errors = {} as FormErrors
  } = $props()

  // svelte-ignore state_referenced_locally -- intentional: form field initialized from prop
  let title = $state(post.title || '')
  // svelte-ignore state_referenced_locally -- intentional: form field initialized from prop
  let content = $state(post.content || '')
  // svelte-ignore state_referenced_locally -- intentional: form field initialized from prop
  let selectedCategories = $state<number[]>(post.categories?.map((c: Category) => c.id) || [])
  // svelte-ignore state_referenced_locally -- intentional: form field initialized from prop
  let privacy = $state(post.privacy || 'full_disclosure')
  let isSubmitting = $state(false)
  let isUploadingImage = $state(false)
  let uploadedImageSignedIds = $state<string[]>([])
  
  function toggleCategory(categoryId: number) {
    if (selectedCategories.includes(categoryId)) {
      selectedCategories = selectedCategories.filter(id => id !== categoryId)
    } else {
      selectedCategories = [...selectedCategories, categoryId]
    }
  }
  
  function handleSubmit(e: Event) {
    e.preventDefault()
    isSubmitting = true

    router.patch(`/boards/${board.slug}/posts/${post.id}`, {
      post: { title, content, privacy_level: privacy },
      category_ids: selectedCategories,
      uploaded_image_signed_ids: uploadedImageSignedIds
    }, {
      onFinish: () => {
        isSubmitting = false
      }
    })
  }

  async function handleImageUpload(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const formData = new FormData()
    formData.append('file', file)

    try {
      isUploadingImage = true
      const response = await fetch('/uploads/image', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken
        },
        body: formData
      })

      const payload = await response.json()
      if (!response.ok || !payload?.signed_id || !payload?.url) {
        toasts.error(payload?.error || '이미지 업로드에 실패했습니다.')
        return
      }

      uploadedImageSignedIds = [...uploadedImageSignedIds, payload.signed_id]
      const markdown = `\n\n![${payload.filename || 'image'}](${payload.url})\n\n`
      content = `${content}${markdown}`
      toasts.success('이미지를 본문에 추가했습니다.')
    } catch {
      toasts.error('이미지 업로드에 실패했습니다.')
    } finally {
      isUploadingImage = false
      input.value = ''
    }
  }
</script>

<div class="space-y-6">
  <!-- Header -->
  <header class="bg-surface border-b border-default">
    <div class="py-4">
      <!-- Breadcrumb -->
      <nav class="flex items-center gap-2 text-caption text-content-tertiary">
        <a href="/boards" class="hover:text-primary transition-colors cursor-pointer">게시판</a>
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
        <a href={`/boards/${board.slug}`} class="hover:text-primary transition-colors cursor-pointer">{board.name}</a>
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
        <a href={`/boards/${board.slug}/posts/${post.id}`} class="hover:text-primary transition-colors cursor-pointer truncate max-w-xs">{post.title}</a>
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
        <span class="text-content">수정</span>
      </nav>
    </div>
  </header>
  
  <main class="py-8 max-w-4xl mx-auto">
    <div class="card p-8">
      <h1 class="text-h1 text-content mb-8">글 수정</h1>
      
      <form onsubmit={handleSubmit} class="space-y-6">
        <!-- Categories -->
        {#if categories.length > 0}
          <div>
            <p class="block text-label text-gray-700 dark:text-dark-text-medium mb-3">카테고리 선택</p>
            <div class="flex flex-wrap gap-2">
              {#each categories as category (category.id)}
                <button
                  type="button"
                  onclick={() => toggleCategory(category.id)}
                  class="px-4 py-2 rounded-xl text-body2 transition-all {selectedCategories.includes(category.id)
                    ? 'text-white shadow-md'
                    : 'text-content-secondary bg-surface-tertiary hover:bg-gray-200 dark:hover:bg-dark-elevated'}"
                  style={selectedCategories.includes(category.id) ? `background-color: ${category.color}` : ''}
                >
                  {category.name}
                </button>
              {/each}
            </div>
          </div>
        {/if}

        <!-- Privacy Selector with Real-time Preview -->
        <div>
          <PrivacySelectorWithPreview
            value={privacy}
            onChange={(level: string) => privacy = level}
            currentUser={{ name: 'Kim ChaeHyun', nickname: '차은' }}
          />
          {#if errors.privacy_level}
            <p class="mt-1 text-caption text-error">{errors.privacy_level}</p>
          {/if}
        </div>

        <!-- Title -->
        <div>
          <label for="title" class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">제목</label>
          <input
            type="text"
            id="title"
            bind:value={title}
            required
            maxlength="100"
            class="input"
            placeholder="제목을 입력하세요"
          />
          {#if errors.title}
            <p class="mt-1 text-caption text-error">{errors.title}</p>
          {/if}
          <p class="mt-1 text-caption text-gray-400 dark:text-dark-text-disabled text-right">{title.length}/100</p>
        </div>
        
        <!-- Content -->
        <div>
          <label for="content" class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">내용</label>
          <div class="flex items-center gap-3 mb-3">
            <label for="edit-image-upload" class="btn btn-secondary btn-sm cursor-pointer">
              {isUploadingImage ? '업로드 중...' : '이미지 첨부'}
            </label>
            <input
              id="edit-image-upload"
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              class="hidden"
              disabled={isUploadingImage}
              onchange={handleImageUpload}
            />
            <span class="text-caption text-content-tertiary">업로드 후 본문에 이미지 마크다운이 자동 삽입됩니다.</span>
          </div>
          <MarkdownToolbar
            value={content}
            textareaId="content"
            onUpdate={(next) => content = next}
          />
          <textarea
            id="content"
            bind:value={content}
            required
            rows="15"
            class="input resize-none"
            placeholder="내용을 입력하세요..."
          ></textarea>
          {#if errors.content}
            <p class="mt-1 text-caption text-error">{errors.content}</p>
          {/if}
        </div>
        
        <!-- Actions -->
        <div class="flex items-center justify-end gap-3 pt-4">
          <a
            href={`/boards/${board.slug}/posts/${post.id}`}
            class="btn btn-secondary btn-md"
          >
            취소
          </a>
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={isSubmitting}
            loadingLabel="수정 중..."
            disabled={!title.trim() || !content.trim()}
          >
            수정하기
          </Button>
        </div>
      </form>
    </div>
  </main>
</div>
